import { useCallback, useState } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../components/Toast';
import { openInGlobe } from '../lib/globe';

/**
 * Launch the 3D globe (optionally with a dataset). Returns `{ openGlobe, opening }`.
 * A 503 means the deployment has no GLOBE_JWT_SECRET; the entry is normally hidden by
 * /api/config already, so this is only the stale-config case.
 */
export function useOpenGlobe() {
  const { selectedOrgId } = useOrg();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [opening, setOpening] = useState(false);

  const openGlobe = useCallback(
    async ({ datasetId, organizationId } = {}) => {
      setOpening(true);
      try {
        await openInGlobe({ organizationId: organizationId || selectedOrgId, datasetId, theme });
      } catch (error) {
        const status = error?.response?.status;
        showToast(
          status === 503
            ? 'The 3D globe is not set up on this deployment yet'
            : error?.response?.data?.detail || 'Could not open the 3D globe',
          'error',
        );
      } finally {
        setOpening(false);
      }
    },
    [selectedOrgId, theme, showToast],
  );

  return { openGlobe, opening };
}

export default useOpenGlobe;
